import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Mail, Settings } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import LeadCaptureModal from './LeadCaptureModal';
import { useLeadCapture } from '../hooks/useLeadCapture';
import { useSmoothScroll } from '../hooks/useSmoothScroll';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { blogPosts, settings } = useData();
  const { isAuthenticated } = useAuth();
  const { isModalOpen, selectedProduct, openModal, closeModal } = useLeadCapture();
  const { scrollToSection } = useSmoothScroll();
  
  // Fallback contact details in case settings are not loaded
  const contactPhone = settings?.contact?.phone || '+91-9977724699';
  const contactEmail = settings?.contact?.email || 'simranindustries143@gmail.com';

  // Detect if we're on a blog post page
  const currentBlogPost = React.useMemo(() => {
    const slug = location.pathname.slice(1); // Remove leading slash
    return blogPosts.find(post => post.slug === slug && post.status === 'published');
  }, [location.pathname, blogPosts]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    // Throttle scroll events for better performance
    let ticking = false;
    const throttledScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', throttledScroll, { passive: true });
    return () => window.removeEventListener('scroll', throttledScroll);
  }, []);

  // Close menu when Escape key is pressed
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when menu is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const handleMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleGetQuote = () => {
    if (currentBlogPost) {
      // If on a blog post, open lead capture modal with blog post title
      openModal(currentBlogPost.title);
    } else {
      // If not on blog post, scroll to contact section
      scrollToSection('contact');
    }
    setIsMenuOpen(false);
  };

  return (
    <header className={`fixed top-0 w-full z-50 transition-all duration-200 ${
      isScrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-sm'
    }`}>
      {/* Top contact bar */}
      <div className="bg-amber-600 text-white py-2 px-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center text-sm space-y-2 sm:space-y-0">
          <div className="flex flex-col sm:flex-row items-center space-y-1 sm:space-y-0 sm:space-x-6">
            <div className="flex items-center space-x-2">
              <Phone size={14} aria-hidden="true" />
              <a 
                href={`tel:${contactPhone.replace(/[^\d+]/g, '')}`}
                className="hover:underline focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-amber-600 rounded text-xs sm:text-sm"
                aria-label={`Call us at ${contactPhone}`}
              >
                {contactPhone}
              </a>
            </div>
            <div className="flex items-center space-x-2">
              <Mail size={14} aria-hidden="true" />
              <a 
                href={`mailto:${contactEmail}`}
                className="hover:underline focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-amber-600 rounded text-xs sm:text-sm"
                aria-label={`Email us at ${contactEmail}`}
              >
                {contactEmail}
              </a>
            </div>
          </div>
          <div className="hidden sm:block">
            <span>Quality Feed Solutions Since 1995</span>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <nav className="px-4 py-4" role="navigation" aria-label="Main navigation">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <Link 
              to="/" 
              className="flex items-center space-x-2 focus:outline-none focus:ring-2 focus:ring-amber-500 rounded-lg p-1"
              aria-label="Simran Industries - Go to homepage"
            >
              <img
                src="https://5.imimg.com/data5/SELLER/Logo/2024/6/429281295/JW/YO/WK/47161101/simran2-120x120.jpg"
                alt="Simran Industries Logo"
                className="w-10 h-10 rounded-full object-cover bg-white border border-amber-600 shadow"
                width="40"
                height="40"
              />
              <div>
                <h1 className="text-xl font-bold text-amber-800">Simran Industries</h1>
                <p className="text-xs text-gray-600">Indore</p>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link 
              to="/"
              className="text-gray-700 hover:text-amber-600 transition-colors duration-200 font-medium focus:outline-none focus:ring-2 focus:ring-amber-500 rounded-lg px-2 py-1"
              aria-label="Go to homepage"
            >
              Home
            </Link>
            <button 
              onClick={() => {
                scrollToSection('about');
                setIsMenuOpen(false);
              }}
              className="text-gray-700 hover:text-amber-600 transition-colors duration-200 font-medium focus:outline-none focus:ring-2 focus:ring-amber-500 rounded-lg px-2 py-1"
              aria-label="Scroll to About section"
            >
              About
            </button>
            <button 
              onClick={() => {
                scrollToSection('products');
                setIsMenuOpen(false);
              }}
              className="text-gray-700 hover:text-amber-600 transition-colors duration-200 font-medium focus:outline-none focus:ring-2 focus:ring-amber-500 rounded-lg px-2 py-1"
              aria-label="Scroll to Products section"
            >
              Products
            </button>
            <button 
              onClick={() => {
                scrollToSection('contact');
                setIsMenuOpen(false);
              }}
              className="text-gray-700 hover:text-amber-600 transition-colors duration-200 font-medium focus:outline-none focus:ring-2 focus:ring-amber-500 rounded-lg px-2 py-1"
              aria-label="Scroll to Contact section"
            >
              Contact
            </button>
            <button 
              onClick={handleGetQuote}
              className="bg-amber-600 text-white px-6 py-2 rounded-lg hover:bg-amber-700 transition-colors duration-200 font-medium focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2"
              aria-label={currentBlogPost ? `Get quote for ${currentBlogPost.title}` : "Get a quote - scroll to contact section"}
            >
              Get Quote
            </button>
            {isAuthenticated && (
              <Link
                to="/admin/dashboard"
                className="text-gray-600 hover:text-amber-600 transition-colors duration-200 font-medium focus:outline-none focus:ring-2 focus:ring-amber-500 rounded-lg px-2 py-1 flex items-center space-x-1"
                aria-label="Go to admin panel"
              >
                <Settings size={16} />
                <span>Admin</span>
              </Link>
            )}
          </div>

          {/* Mobile Contact Info */}
          <div className="md:hidden flex items-center space-x-3">
            <a 
              href={`tel:${contactPhone.replace(/[^\d+]/g, '')}`}
              className="flex items-center space-x-1 text-amber-600 hover:text-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 rounded p-1"
              aria-label={`Call us at ${contactPhone}`}
            >
              <Phone size={16} />
              <span className="text-xs font-medium hidden xs:inline">Call</span>
            </a>
            <a 
              href={`mailto:${contactEmail}`}
              className="flex items-center space-x-1 text-amber-600 hover:text-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 rounded p-1"
              aria-label={`Email us at ${contactEmail}`}
            >
              <Mail size={16} />
              <span className="text-xs font-medium hidden xs:inline">Email</span>
            </a>
          </div>

          {/* Mobile menu button */}
          <button 
            className="md:hidden p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
            onClick={handleMenuToggle}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-menu"
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMenuOpen ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div 
            id="mobile-menu"
            className="md:hidden absolute top-full left-0 w-full bg-white shadow-lg border-t"
            role="navigation"
            aria-label="Mobile navigation"
          >
            {/* Mobile Contact Info */}
            <div className="bg-amber-50 border-b border-amber-200 px-4 py-3">
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Phone size={16} className="text-amber-600" />
                  <a 
                    href={`tel:${contactPhone.replace(/[^\d+]/g, '')}`}
                    className="text-amber-700 hover:text-amber-800 font-medium text-sm"
                  >
                    {contactPhone}
                  </a>
                </div>
                <div className="flex items-center space-x-2">
                  <Mail size={16} className="text-amber-600" />
                  <a 
                    href={`mailto:${contactEmail}`}
                    className="text-amber-700 hover:text-amber-800 font-medium text-sm"
                  >
                    {contactEmail}
                  </a>
                </div>
              </div>
            </div>
            
            <div className="py-4 px-4 space-y-4">
              <Link 
                to="/"
                className="block w-full text-left text-gray-700 hover:text-amber-600 transition-colors duration-200 font-medium py-2 px-3 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-amber-500"
                onClick={() => setIsMenuOpen(false)}
                aria-label="Go to homepage"
              >
                Home
              </Link>
              <button 
                onClick={() => {
                  scrollToSection('about');
                  setIsMenuOpen(false);
                }}
                className="block w-full text-left text-gray-700 hover:text-amber-600 transition-colors duration-200 font-medium py-2 px-3 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-amber-500"
                aria-label="Scroll to About section"
              >
                About
              </button>
              <button 
                onClick={() => {
                  scrollToSection('products');
                  setIsMenuOpen(false);
                }}
                className="block w-full text-left text-gray-700 hover:text-amber-600 transition-colors duration-200 font-medium py-2 px-3 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-amber-500"
                aria-label="Scroll to Products section"
              >
                Products
              </button>
              <button 
                onClick={() => {
                  scrollToSection('contact');
                  setIsMenuOpen(false);
                }}
                className="block w-full text-left text-gray-700 hover:text-amber-600 transition-colors duration-200 font-medium py-2 px-3 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-amber-500"
                aria-label="Scroll to Contact section"
              >
                Contact
              </button>
              <button 
                onClick={handleGetQuote}
                className="w-full bg-amber-600 text-white px-6 py-3 rounded-lg hover:bg-amber-700 transition-colors duration-200 font-medium focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2"
                aria-label={currentBlogPost ? `Get quote for ${currentBlogPost.title}` : "Get a quote - scroll to contact section"}
              >
                Get Quote
              </button>
              {isAuthenticated && (
                <Link
                  to="/admin/dashboard"
                  className="block w-full text-left text-gray-600 hover:text-amber-600 transition-colors duration-200 font-medium py-2 px-3 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-amber-500 flex items-center space-x-2"
                  onClick={() => setIsMenuOpen(false)}
                  aria-label="Go to admin panel"
                >
                  <Settings size={16} />
                  <span>Admin Panel</span>
                </Link>
              )}
            </div>
          </div>
        )}
      </nav>
      
      {/* Lead Capture Modal */}
      <LeadCaptureModal
        isOpen={isModalOpen}
        onClose={closeModal}
        productName={selectedProduct}
      />
    </header>
  );
};

export default Header;
